import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import { PayrexxInstanceStatus, Vendor } from '../models/entities/vendor';
import { ApiGetSuccessResponse } from '../models/common/api-get-success-response';
import { UpdateVendorRequest } from '../requests/vendors/update-vendor.request';
import { UpdateTwoFactorAuthRequest } from '../requests/vendors/update-two-factor-auth.request';
import { ListingVendor } from '../models/listing-vendor';

@Injectable({
  providedIn: 'root',
})
export class VendorsService {
  // Mémorise la liste publique : évite un rechargement à chaque retour sur la page
  private listingVendorsCache?: Promise<ApiGetSuccessResponse<ListingVendor[]>>;

  constructor(private apiService: ApiService) {}

  findAllForListing(): Promise<ApiGetSuccessResponse<ListingVendor[]>> {
    if (!this.listingVendorsCache) {
      this.listingVendorsCache = firstValueFrom(
        this.apiService.get('vendors'),
      ).catch((exception) => {
        // En cas d'échec, on n'empoisonne pas le cache : on pourra réessayer
        this.listingVendorsCache = undefined;
        throw exception;
      });
    }

    return this.listingVendorsCache;
  }

  store(formData: object) {
    return this.apiService.post('vendors', formData);
  }

  findOne(slug: string): Observable<Vendor> {
    return this.apiService
      .get('vendors/' + slug)
      .pipe(map((response: ApiGetSuccessResponse<Vendor>) => response.data));
  }

  update(request: UpdateVendorRequest) {
    if (request.logo || request.general_terms) {
      return this.apiService.post(
        `vendors`,
        ApiService.buildFormData(request, true),
      );
    } else {
      return this.apiService.put(`vendors`, request);
    }
  }

  createPayrexxMerchantInstance(): Promise<void> {
    return this.apiService.post(`vendors/setup-payrexx`, {});
  }

  findPayrexxVerificationStatus(): Promise<
    ApiGetSuccessResponse<{ status: PayrexxInstanceStatus }>
  > {
    return firstValueFrom(
      this.apiService.get(`vendors/payrexx-verification-status`, {}),
    );
  }

  updateTwoFactorAuth(request: UpdateTwoFactorAuthRequest) {
    return this.apiService.put(`vendors/two-factor-authentication`, request);
  }

  updateTestMode(request: { test_mode: boolean }) {
    return this.apiService.put(`vendors/test-mode`, request);
  }

  updateBookingPeriodConstraint(request: {
    booking_period_constraint: string | null;
  }) {
    return this.apiService.put(`vendors/booking-period-constraint`, request);
  }

  updateGracePeriod(request: { grace_period_hours: number | null }) {
    return this.apiService.put(`vendors/grace-period`, request);
  }

  storePaymentMethod(request: { name: string }) {
    return this.apiService.post(`vendors/payment-methods`, request);
  }

  deletePaymentMethod(request: { name: string }) {
    return this.apiService.delete(`vendors/payment-methods`, request);
  }
}
